import { apiFetchAuthed } from '../apiHelpers';
import useUserStore from '../stores/useUserStore';
import type { Adoption, PlanPoint, PointRevision, PointsSummary, PostThreadInput, ThreadItem } from './pointsTypes';

interface Versioned {
  version: number;
}

const onUnauthorized = () => useUserStore.getState().logout();

const call = (url: string, options: Record<string, unknown> = {}) => apiFetchAuthed(url, { onUnauthorized, ...options });

const base = (planId: string) => `/node-plans/${encodeURIComponent(planId)}`;
const nodePath = (planId: string, nodeId: string) => `${base(planId)}/nodes/${encodeURIComponent(nodeId)}`;
const pointPath = (planId: string, nodeId: string, pointId: string) =>
  `${nodePath(planId, nodeId)}/points/${encodeURIComponent(pointId)}`;
const threadPath = (pointId: string) => `/messages/node_plan_point/${encodeURIComponent(pointId)}`;

export const getSummary = (planId: string): Promise<PointsSummary> => call(`${base(planId)}/points/summary`);

export const listPoints = (planId: string, nodeId: string): Promise<{ points: PlanPoint[] }> =>
  call(`${nodePath(planId, nodeId)}/points`);

export const createPoint = (planId: string, nodeId: string, body: string): Promise<{ point: PlanPoint } & Versioned> =>
  call(`${nodePath(planId, nodeId)}/points`, { method: 'POST', body: { body } });

export const updatePoint = (
  planId: string,
  nodeId: string,
  pointId: string,
  body: string,
): Promise<{ point: PlanPoint } & Versioned> =>
  call(pointPath(planId, nodeId, pointId), { method: 'PATCH', body: { body } });

export const deletePoint = (planId: string, nodeId: string, pointId: string): Promise<Versioned> =>
  call(pointPath(planId, nodeId, pointId), { method: 'DELETE' });

export const votePoint = (
  planId: string,
  nodeId: string,
  pointId: string,
): Promise<{ voted: boolean; votes: number } & Versioned> =>
  call(`${pointPath(planId, nodeId, pointId)}/vote`, { method: 'POST' });

export const listRevisions = (planId: string, nodeId: string, pointId: string): Promise<{ revisions: PointRevision[] }> =>
  call(`${pointPath(planId, nodeId, pointId)}/revisions`);

export const listAdoptions = (planId: string, nodeId: string): Promise<{ adoptions: Adoption[] }> =>
  call(`${nodePath(planId, nodeId)}/adoptions`);

export const adoptPoint = (planId: string, nodeId: string, pointId: string): Promise<{ adoption: Adoption } & Versioned> =>
  call(`${pointPath(planId, nodeId, pointId)}/adopt`, { method: 'POST' });

export const unadoptPoint = (planId: string, nodeId: string, pointId: string): Promise<Versioned> =>
  call(`${pointPath(planId, nodeId, pointId)}/unadopt`, { method: 'POST' });

// Silent: a read mark failing is nothing the person needs to hear about.
export const markSeen = (planId: string, nodeId: string): Promise<{ last_seen_seq: number }> =>
  call(`${nodePath(planId, nodeId)}/seen`, { method: 'POST', silent: true });

export const listThread = (pointId: string): Promise<{ items: ThreadItem[] }> => call(threadPath(pointId));

export const postThread = (pointId: string, input: PostThreadInput): Promise<ThreadItem> =>
  call(threadPath(pointId), { method: 'POST', body: input });
